import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { AppNotification, NotificationKind } from '../db/database'
import {
  currentAccountId, listNotifications, markAllRead, removeNotification, setRead,
} from '../db/notifications'
import { formatDay } from '../lib/dates'
import { parseTarget, type NotificationTarget } from '../lib/notification-target'
import { useSwipe } from '../lib/swipe'
import { Icon, type IconName } from './Icon'
// La confirmation réutilise l'habillage de `ConfirmDeleteSheet` — même geste,
// même enjeu. Importé explicitement plutôt que par ricochet : le jour où cette
// feuille se monte seule, le style doit venir avec.
import './ConfirmDeleteSheet.css'
import './NotificationsSheet.css'

/** Ce que chaque genre de notification montre comme icône. */
const ICONS: Record<NotificationKind, IconName> = {
  reminder: 'calendar',
  budget: 'wallet',
  goal: 'savings',
  cycle: 'history',
  shared: 'person',
  system: 'bell',
}

function isRead(notification: AppNotification): boolean {
  // `?? null` : une ligne écrite avant l'existence du champ vaut `undefined`.
  return (notification.read_at ?? null) !== null
}

/**
 * Une notification, glissable des deux côtés.
 *
 * Les deux sens reprennent ceux d'une opération, et pour la même raison : un
 * geste appris une fois doit valoir partout. Vers la GAUCHE l'action douce et
 * réversible — ici lu / non lu ; vers la DROITE, ce qui retire.
 */
function NotificationRow(
  { notification, onOpen, onDelete }: {
    notification: AppNotification
    onOpen: (notification: AppNotification) => void
    onDelete: (notification: AppNotification) => void
  },
) {
  const read = isRead(notification)
  const swipe = useSwipe({
    enabled: true,
    onLeft: () => { void setRead(notification.id, !read) },
    onRight: () => onDelete(notification),
  })

  return (
    <li className="notif-item">
      <span className="notif-clip">
        {/* Ce qui attend derrière la ligne : l'icône dit ce que le geste va
            faire, AVANT de le faire. */}
        {swipe.dx !== 0 && (
          <span className={`notif-hint ${swipe.dx < 0 ? 'is-read' : 'is-delete'}
                            ${swipe.armed ? 'is-armed' : ''}`}
                aria-hidden="true">
            <Icon name={swipe.dx < 0 ? (read ? 'mail' : 'check') : 'trash'} size={20} />
          </span>
        )}

        <span className="notif-slide"
              style={swipe.dx ? { transform: `translateX(${swipe.dx}px)` } : undefined}>
          <button type="button" className={`notif ${read ? 'is-read' : 'is-unread'}`}
                  {...swipe.handlers}
                  onClick={() => { if (!swipe.consume()) onOpen(notification) }}>
            <span className="notif-icon" aria-hidden="true">
              <Icon name={ICONS[notification.kind] ?? 'bell'} size={18} />
            </span>
            <span className="notif-body">
              <span className="notif-title">{notification.title}</span>
              {notification.body && <span className="notif-text">{notification.body}</span>}
              {/* La date de l'ÉVÉNEMENT, pas celle de son arrivée ici : un
                  rappel du 5 rapatrié le 7 reste un rappel du 5. */}
              <span className="notif-when">{formatDay(notification.occurred_at.slice(0, 10))}</span>
            </span>
            {/* Le point de non-lu est doublé par un mot pour les lecteurs
                d'écran : une pastille de couleur n'annonce rien. */}
            {!read && (
              <>
                <span className="notif-dot" aria-hidden="true" />
                <span className="visually-hidden">Non lue</span>
              </>
            )}
          </button>
        </span>
      </span>
    </li>
  )
}

/**
 * Le centre de notifications.
 *
 * Il liste TOUT ce que l'app a eu à dire, lu ou non — c'est une archive, pas
 * une file d'attente : une notification lue reste consultable, parce qu'elle
 * est souvent la seule trace de ce qui s'est passé pendant une absence.
 *
 * Une boîte par PERSONNE : la liste est celle du compte de cet appareil, jamais
 * celle du portefeuille. Deux membres d'un même portefeuille ont deux boîtes —
 * sinon le premier qui lit marquerait lu pour l'autre.
 *
 * ⚠️ **Rien ne l'alimente encore** (2026-09-05). Les sources — rappels
 * récurrents, enveloppe dépassée, objectif atteint, décisions d'un autre membre
 * — sont des features à venir, qui écriront par `notify()`. La liste est donc
 * vide en pratique, et l'écran vide est écrit pour ça.
 */
export function NotificationsSheet(
  { onClose, onOpenTarget }: {
    onClose: () => void
    /**
     * Où mène la notification touchée. Remonté à `App` plutôt que traité ici :
     * ouvrir la feuille d'une journée ou le détail d'une opération suppose
     * l'état de l'application, pas celui de cette liste.
     */
    onOpenTarget: (target: NotificationTarget) => void
  },
) {
  const accountId = currentAccountId()
  const items = useLiveQuery(
    async () => (accountId ? listNotifications(accountId) : []), [accountId])
  const [deleting, setDeleting] = useState<AppNotification | null>(null)

  const unread = (items ?? []).filter((n) => !isRead(n)).length

  function open(notification: AppNotification) {
    // Ouvrir vaut lecture : c'est le geste qui dit « j'ai vu ».
    if (!isRead(notification)) void setRead(notification.id, true)
    // La destination vient de la base, donc potentiellement d'un serveur : elle
    // passe par une liste blanche (`parseTarget`) avant d'être suivie. Rien de
    // reconnu ⇒ la notification se contente d'être lue.
    const target = parseTarget(notification.route)
    if (!target) return
    onClose()
    onOpenTarget(target)
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label="Notifications">
      <div className="sheet notif-sheet">
        <header className="sheet-head">
          <h2>Notifications</h2>
          {unread > 0 && (
            <button type="button" className="notif-all"
                    onClick={() => { if (accountId) void markAllRead(accountId) }}>
              Tout marquer comme lu
            </button>
          )}
          <button type="button" className="sheet-close" aria-label="Fermer"
                  onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </header>

        {items === undefined ? (
          <p className="notif-empty" />
        ) : items.length === 0 ? (
          <p className="notif-empty">
            Rien pour l'instant. Les rappels et les alertes de budget
            s'afficheront ici.
          </p>
        ) : (
          <>
            <ul className="notif-list">
              {items.map((notification) => (
                <NotificationRow key={notification.id} notification={notification}
                                 onOpen={open} onDelete={setDeleting} />
              ))}
            </ul>
            <p className="notif-help">
              Glisse une notification vers la gauche pour la marquer lue,
              vers la droite pour la supprimer.
            </p>
          </>
        )}
      </div>

      {/* La confirmation, par-dessus la feuille. Le glissement est fait pour
          être rapide, et rapide veut dire déclenché par mégarde — on redit donc
          CE QU'ON SUPPRIME plutôt que de poser une question abstraite. */}
      {deleting && (
        <div className="sheet-backdrop" role="alertdialog" aria-modal="true"
             aria-label="Supprimer cette notification">
          <div className="sheet confirm-sheet">
            <header><h2>Supprimer cette notification ?</h2></header>
            <p className="confirm-what"><strong>{deleting.title}</strong></p>
            <p className="confirm-help">
              Elle disparaîtra de cette liste. Ce qu'elle annonçait, lui, ne
              change pas.
            </p>
            <div className="confirm-actions">
              <button type="button" className="confirm-go"
                      onClick={() => {
                        const cible = deleting
                        setDeleting(null)
                        void removeNotification(cible.id)
                      }}>
                <Icon name="trash" size={16} /> Supprimer
              </button>
              <button type="button" className="confirm-cancel"
                      onClick={() => setDeleting(null)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
